/**
 * The phone navigation (dc.html:540-547).
 *
 * `bottom: calc(12px + env(safe-area-inset-bottom))` is the whole reason index.html carries
 * `viewport-fit=cover`. Without that meta the inset resolves to 0px, the bar drops 34px lower on a
 * notched handset, and the home indicator sits across "Reminders" and "Review" — a bar you cannot
 * reliably tap looks like a bar that does not work.
 *
 * `.ft-bnav` has no display rule of its own below 1120px (the grid comes from the inline style
 * here) and `display:none !important` above it. The `!important` is load-bearing: it has to beat
 * this element's own inline `display:grid`, which would otherwise win the cascade outright and
 * leave both navigations on screen at desktop width.
 *
 * Same `items` array as the sidebar, built once by `AppShell` — the two cannot drift.
 */

import type { ReactElement } from 'react'

import { SpriteIcon } from '../model/icons'
import type { NavItemView, ScreenId } from '../model/nav'

export interface BottomNavProps {
  readonly items: readonly NavItemView[]
  readonly onNavigate: (screen: ScreenId) => void
}

export function BottomNav({ items, onNavigate }: BottomNavProps): ReactElement {
  return (
    <nav
      className="ft-bnav"
      aria-label="Main"
      style={{
        position: 'fixed',
        left: '14px',
        right: '14px',
        bottom: 'calc(12px + env(safe-area-inset-bottom))',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '2px',
        padding: '7px',
        background: 'var(--color-surface)',
        borderRadius: '999px',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 30,
      }}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-current={item.current}
          onClick={() => onNavigate(item.id)}
          style={{
            position: 'relative',
            border: 0,
            cursor: 'pointer',
            // 52px, comfortably over the 44px tap-target floor — this is the only navigation a
            // phone user has.
            minHeight: '52px',
            borderRadius: '999px',
            display: 'grid',
            placeItems: 'center',
            alignContent: 'center',
            gap: '3px',
            font: 'inherit',
            fontSize: '10.5px',
            fontWeight: 700,
            background: item.background,
            color: item.foreground,
          }}
        >
          <SpriteIcon icon={item.icon} />
          {/* `short`, not `label` — "Dog profile" does not fit a quarter of a 320px screen. */}
          <span>{item.short}</span>
          {item.badge ? (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '9px',
                right: '26%',
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: 'var(--color-accent)',
              }}
            />
          ) : null}
        </button>
      ))}
    </nav>
  )
}
